import { isError } from '@ifixit/helpers';
import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { useState, useCallback } from 'react';
import { CustomAnalyticsEvent, trackAnalyticsCustomEvent } from '@ifixit/analytics';

/**
 * A regex to validate email against simple mistakes
 */
export const EMAIL_VALIDATION_REGEX = /^\S+@\S+\.\S+$/;

export enum SubscriptionStatus {
   Idle = 'idle',
   Subscribing = 'subscribing',
   Subscribed = 'subscribed',
}

export interface Subscription {
   status: SubscriptionStatus;
   error?: string;
}

export interface SubscribeFn {
   (email: string): void;
}

type AnalyticsSource = 'Modal' | 'Footer';

type useSubscribeToNewsletterParams = {
   storeCode: string;
   analyticsSource: AnalyticsSource;
};

export function getNewsletterSubscriptionEvent(
   analyticsSource: AnalyticsSource,
   eventAction: string
): CustomAnalyticsEvent {
   return {
      eventCategory: `Newsletter Signup`,
      eventAction: `Newsletter Signup ${analyticsSource} ${eventAction}`,
   };
}

export function useSubscribeToNewsletter({
   storeCode,
   analyticsSource,
}: useSubscribeToNewsletterParams): [Subscription, SubscribeFn] {
   const client = useIFixitApiClient();
   const [state, setState] = useState<Subscription>({
      status: SubscriptionStatus.Idle,
   });

   // eslint-disable-next-line react-hooks/preserve-manual-memoization
   const subscribe = useCallback<SubscribeFn>(async email => {
      if (EMAIL_VALIDATION_REGEX.test(email)) {
         setState(current => ({
            ...current,
            status: SubscriptionStatus.Subscribing,
         }));
         try {
            await client.post('cart/newsletter/subscribe', 'subscribe-to-newsletter', {
               body: JSON.stringify({
                  email,
                  storeCode,
                  source: analyticsSource,
               }),
            });
            setState(() => ({
               status: SubscriptionStatus.Subscribed,
               error: undefined,
            }));
            trackAnalyticsCustomEvent(
               getNewsletterSubscriptionEvent(analyticsSource, 'Subscribed')
            );
         } catch (error) {
            let message: string;
            if (isError(error)) {
               message = 'Error trying to subscribe to newsletter.';
            }
            setState(() => ({
               status: SubscriptionStatus.Idle,
               error: message || 'server error',
            }));
         }
      } else {
         setState(current => ({
            ...current,
            error: 'Please insert a valid email',
         }));
      }
   }, []);

   return [state, subscribe];
}
